/**
 * CompanyBudgetControl — inline editor for a company's monthly Eva cap (SF-3737).
 *
 * The company-detail page already shows usage and a "% of Monthly Cap" tile but
 * had no way to SET the company's monthly cap from the UI — the sf-api endpoint
 * (PUT /admin/eva/budget-override/:companyId) exists and is wired to
 * enforcement, it just had no UI. This card surfaces the current effective cap,
 * an editable numeric input, and a Save button that only appears once the value
 * differs from the loaded value. A "Revert to default" action clears the
 * override (sends null) when one is set.
 *
 * Permission-aware: writes require `admin:eva:budget:write`. Read-only operators
 * see a disabled input + a tooltip explaining why; a server-side 403 (defence in
 * depth) surfaces a clear error toast. On a successful save the parent refetches
 * so the "% of Monthly Cap" figures update.
 */
import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import {
  Button,
  Card,
  InputNumber,
  Skeleton,
  Space,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';

import { hasPermission } from '../../utils/permissions';
import { useCanEditCompany } from '../companies/api/editableCompaniesClient';
import { formatCurrency, EM_DASH, parseMoney } from '../../utils/format';
import {
  EVA_BUDGET_READ_PERMISSION,
  EVA_BUDGET_WRITE_PERMISSION,
  budgetUpdateMutationArgs,
  useCompanyBudgetOverride,
  useSetCompanyBudgetOverride,
  type EvaBudgetOverride,
} from './api/evaBudgetClient';

const { Text } = Typography;

export interface CompanyBudgetControlProps {
  /** Company ObjectId whose monthly cap is being edited. */
  companyId: string;
  /** Gate the read request on access-control resolution (defaults to true). */
  enabled?: boolean;
  /**
   * Called after a successful save so the parent can refetch usage and refresh
   * the dependent "% of Monthly Cap" figures.
   */
  onSaved?: () => void;
}

/** Pull a human-readable message out of an sf-api axios error (incl. 403). */
function errorMessage(error: unknown, fallback: string): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  if (axiosErr?.response?.status === 403) {
    return 'You do not have permission to change this company\u2019s Eva budget.';
  }
  return axiosErr?.response?.data?.message || fallback;
}

/** Inline-editable monthly Eva budget control for a single company. */
export const CompanyBudgetControl: React.FC<CompanyBudgetControlProps> = ({
  companyId,
  enabled = true,
  onSaved,
}) => {
  // Belt-and-braces UX: hide the write affordances when the operator lacks the
  // write permission. The server enforces this too (403) — see errorMessage.
  const { data: permissions, isLoading: permsLoading } = usePermissions<string[]>();
  const perms = permissions ?? [];
  const canRead = hasPermission(perms, EVA_BUDGET_READ_PERMISSION);
  // SF-4668: staff with a company list may only change the companies on it.
  const { canEditCompany } = useCanEditCompany(companyId);
  const canWrite = hasPermission(perms, EVA_BUDGET_WRITE_PERMISSION) && canEditCompany;

  // Only fire the GET when the operator actually holds budget:read. The budget
  // endpoint is gated on admin:eva:budget:read independently of usage:read, so
  // an operator who can see Eva usage but lacks budget:read would otherwise hit
  // a silent 403 (errorNotification suppressed) and see an unexplained empty
  // card. Gating the request lets us render an explicit "no access" state.
  // Wait for permissions to resolve before deciding so we don't fire a doomed
  // request (or flash the denial) during the brief usePermissions load.
  const budgetQuery = useCompanyBudgetOverride(companyId, {
    enabled: enabled && !permsLoading && canRead,
  });
  const budget: EvaBudgetOverride | undefined = budgetQuery.data?.data;
  const setBudget = useSetCompanyBudgetOverride();

  // The cap currently enforced (override value or env default) and whether a
  // per-company override is set. Null-guarded — the input falls back to null.
  const effectiveCap = parseMoney(budget?.effectiveCapUsd);
  const isOverride = budget?.isOverride === true;

  // Local edit state, seeded from the loaded effective cap. `null` is a valid
  // value while the field is empty; we only enable Save when it actually moves.
  const [value, setValue] = useState<number | null>(null);

  // Re-seed the input whenever fresh data lands (initial load or post-save
  // refetch) so the "dirty" comparison below is against the persisted value.
  useEffect(() => {
    setValue(effectiveCap);
    // budget identity changes on every (re)fetch; effectiveCap is derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budget]);

  // Save only appears/enables when the edited value differs from what loaded.
  const isDirty = useMemo(() => value !== effectiveCap, [value, effectiveCap]);

  const saving = setBudget.isLoading;

  const handleSave = () => {
    // Guard: a non-negative finite number is required to upsert (mirrors the
    // sf-api validator). The InputNumber min=0 enforces this in the happy path.
    if (value === null || !Number.isFinite(value) || value < 0) {
      message.error('Enter a non-negative monthly cap, or revert to default.');
      return;
    }
    setBudget.mutate(budgetUpdateMutationArgs(companyId, { monthlyCostLimitUsd: value }), {
      onSuccess: () => {
        message.success('Company Eva budget updated.');
        // Refetch this control's data so isOverride/effectiveCap reflect the
        // write, and let the parent refresh the dependent "% of cap" figures.
        budgetQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => {
        message.error(errorMessage(error, 'Failed to update the Eva budget.'));
      },
    });
  };

  const handleRevert = () => {
    // null → delete the override on sf-api, reverting to the env default.
    setBudget.mutate(budgetUpdateMutationArgs(companyId, { monthlyCostLimitUsd: null }), {
      onSuccess: () => {
        message.success('Reverted to the default Eva budget.');
        budgetQuery.refetch?.();
        onSaved?.();
      },
      onError: (error) => {
        message.error(errorMessage(error, 'Failed to revert the Eva budget.'));
      },
    });
  };

  const writeDisabledHint = canWrite
    ? undefined
    : !canEditCompany
      ? 'This company is not on your list of companies, so you can only view it.'
      : 'You do not have permission to change the Eva budget (admin:eva:budget:write).';

  return (
    <Card style={{ height: '100%' }}>
      {permsLoading ? (
        // Permissions still resolving — show the skeleton, not a false "no
        // access" denial, until we actually know the operator's grants.
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
      ) : !canRead ? (
        // Budget read is gated independently of usage read — be explicit rather
        // than rendering a blank/empty card when the operator can't see it.
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <DollarOutlined /> Company Monthly Eva Budget
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            You do not have permission to view the Eva budget
            (admin:eva:budget:read).
          </Text>
        </Space>
      ) : budgetQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '40%' }} />
      ) : budgetQuery.isError ? (
        // Surface a load failure inline (the query suppresses the global toast).
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <DollarOutlined /> Company Monthly Eva Budget
          </Text>
          <Text type="danger" style={{ fontSize: 12 }}>
            Could not load the Eva budget. Please retry.
          </Text>
          <Button size="small" onClick={() => budgetQuery.refetch?.()}>
            Retry
          </Button>
        </Space>
      ) : (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Text type="secondary">
            <DollarOutlined /> Company Monthly Eva Budget
          </Text>
          <Space wrap align="center" size="middle">
            <Tooltip title={writeDisabledHint}>
              {/* AntD tooltips don't fire on a disabled control — the disabled
                  element swallows pointer events. Wrap it in an inline-block
                  span (only when disabled) so the hint is hoverable for
                  read-only operators; when enabled, render the input directly
                  to keep the normal layout/focus behaviour. */}
              {!canWrite ? (
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                  <InputNumber
                    aria-label="Company monthly Eva budget (USD)"
                    value={value}
                    min={0}
                    step={50}
                    disabled
                    prefix="$"
                    style={{ width: 200, pointerEvents: 'none' }}
                    onChange={(next) => setValue(typeof next === 'number' ? next : null)}
                  />
                </span>
              ) : (
                <InputNumber
                  aria-label="Company monthly Eva budget (USD)"
                  value={value}
                  min={0}
                  step={50}
                  disabled={saving}
                  prefix="$"
                  style={{ width: 200 }}
                  onChange={(next) => setValue(typeof next === 'number' ? next : null)}
                />
              )}
            </Tooltip>
            {/* Save only appears once the value differs from the loaded cap. */}
            {canWrite && isDirty && (
              <Button type="primary" loading={saving} onClick={handleSave}>
                Save
              </Button>
            )}
            {/* Revert is offered only when a per-company override is actually set. */}
            {canWrite && isOverride && (
              <Button danger loading={saving} onClick={handleRevert}>
                Revert to default
              </Button>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {effectiveCap !== null
              ? `Effective cap: ${formatCurrency(effectiveCap)} / month${
                  isOverride ? ' (per-company override)' : ' (default)'
                }`
              : EM_DASH}
          </Text>
        </Space>
      )}
    </Card>
  );
};
